import { useState, useEffect, useCallback, useMemo } from 'react';
import { Plus, Settings, CheckCircle, Route, User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSpinner, MetricCard, MetricCardsGrid, EmptyState, Pagination } from '@/shared/components';
import { rulesApi } from './api';
import { RuleTable, CreateRuleModal } from './components';
import type { Rule, RuleType, RuleConfig, PaginatedRules } from './types';

const DEFAULT_LIMIT = 20;

export function RulesPage() {
  const [rules, setRules] = useState<Rule[]>([]);
  const [loading, setLoading] = useState(true);
  const [_error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState<Rule | null>(null);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState<PaginatedRules['pagination'] | null>(null);

  const fetchRules = useCallback(async (background = false, targetPage = page) => {
    if (!background) {
      setLoading(true);
    }
    setError(null);
    try {
      const result = await rulesApi.list({ page: targetPage, limit: DEFAULT_LIMIT });
      setRules(result.data);
      setPagination(result.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch rules');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchRules(false, page);
  }, [page]);

  const handlePrevious = () => setPage((p) => Math.max(1, p - 1));
  const handleNext = () => setPage((p) => p + 1);

  const metrics = useMemo(() => {
    const active = rules.filter((r) => r.enabled).length;
    const journeyRules = rules.filter((r) => r.type === 'journey').length;
    const affinityRules = rules.filter((r) => r.type === 'affinity').length;
    const total = pagination?.total ?? rules.length;
    return { total, active, journeyRules, affinityRules };
  }, [rules, pagination]);

  const handleOpenCreate = () => {
    setEditingRule(null);
    setModalOpen(true);
  };

  const handleEditRule = (rule: Rule) => {
    setEditingRule(rule);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingRule(null);
  };

  const handleSaveRule = async (data: { name: string; type: RuleType; config: RuleConfig }) => {
    setSaving(true);
    try {
      if (editingRule) {
        await rulesApi.update(editingRule.id, {
          name: data.name,
          config: data.config,
        });
      } else {
        await rulesApi.create({
          name: data.name,
          type: data.type,
          enabled: true,
          config: data.config,
        });
      }
      handleCloseModal();
      fetchRules(true, page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (id: string) => {
    const previousRules = rules;
    setRules(rules.filter((r) => r.id !== id));

    try {
      await rulesApi.delete(id);
    } catch (err) {
      setRules(previousRules);
      setError(err instanceof Error ? err.message : 'Failed to delete rule');
    }
  };

  const handleReorder = async (reorderedRules: Rule[]) => {
    const previousRules = rules;
    setRules(reorderedRules);

    try {
      const ruleIds = reorderedRules.map((r) => r.id);
      await rulesApi.reorder(ruleIds);
    } catch (err) {
      setRules(previousRules);
      setError(err instanceof Error ? err.message : 'Failed to reorder rules');
    }
  };

  if (loading && rules.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <LoadingSpinner />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <MetricCardsGrid>
        <MetricCard
          title="Total Rules"
          value={metrics.total}
          icon={Settings}
          color="default"
        />
        <MetricCard
          title="Active"
          value={metrics.active}
          icon={CheckCircle}
          color="success"
          subtitle={metrics.total > 0 ? `${((metrics.active / metrics.total) * 100).toFixed(0)}% enabled` : undefined}
        />
        <MetricCard
          title="Journey Rules"
          value={metrics.journeyRules}
          icon={Route}
          color="default"
        />
        <MetricCard
          title="Affinity Rules"
          value={metrics.affinityRules}
          icon={User}
          color="default"
        />
      </MetricCardsGrid>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Attribution Rules</CardTitle>
              <CardDescription>
                Configure rules for assigning orders to distributors
              </CardDescription>
            </div>
            <Button onClick={handleOpenCreate}>
              <Plus className="h-4 w-4 mr-2" />
              Create Rule
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {rules.length === 0 ? (
            <EmptyState
              heading="No rules configured"
              action={{ content: 'Create Rule', onAction: handleOpenCreate }}
            >
              <p>Create attribution rules to assign orders to distributors.</p>
            </EmptyState>
          ) : (
            <>
              <RuleTable
                rules={rules}
                onEdit={handleEditRule}
                onDelete={handleDeleteRule}
                onReorder={handleReorder}
              />
              {pagination && pagination.totalPages > 1 && (
                <div className="mt-4">
                  <Pagination
                    page={page}
                    totalPages={pagination.totalPages}
                    onPrevious={handlePrevious}
                    onNext={handleNext}
                  />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      <CreateRuleModal
        open={modalOpen}
        rule={editingRule}
        saving={saving}
        onClose={handleCloseModal}
        onSave={handleSaveRule}
      />
    </div>
  );
}
